<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <!-- Header -->
            <div class="row mb-3">
              <div class="col-sm-4">
                <div class="search-box d-inline-block" style="width:100%">
                  <div class="position-relative">
                    <input
                      type="text"
                      class="form-control"
                      v-model="search"
                      placeholder="Buscar template..."
                      @keyup.enter="doSearch"
                    />
                    <i class="bx bx-search-alt search-icon"></i>
                  </div>
                </div>
              </div>
              <div class="col-sm-8">
                <div class="float-sm-end d-flex">
                  <select v-model="statusFilter" class="custom-select mr-2" style="width:140px" @change="doSearch">
                    <option value="">Todos</option>
                    <option value="APPROVED">Aprovados</option>
                    <option value="PENDING">Pendentes</option>
                    <option value="REJECTED">Rejeitados</option>
                  </select>
                  <button v-if="canCreate" type="button" class="btn btn-light btn-rounded mb-2 mr-2" @click="handleSync" :disabled="syncing">
                    <i class="bx bx-refresh mr-1" :class="{ 'bx-spin': syncing }"></i>
                    {{ syncing ? 'Sincronizando...' : 'Sincronizar' }}
                  </button>
                  <router-link v-if="canCreate" to="/wa/template/create" class="btn btn-success btn-rounded mb-2">
                    <i class="mdi mdi-plus mr-1"></i> Criar template
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Table -->
            <div class="table-responsive">
              <table class="table table-centered table-nowrap">
                <thead>
                  <tr>
                    <th width="30%">Nome</th>
                    <th width="12%">Categoria</th>
                    <th width="10%">Idioma</th>
                    <th width="12%">Status</th>
                    <th width="15%">Atualizado</th>
                    <th width="10%">Visualizar</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="tpl in filteredTemplates" :key="tpl.id">
                    <td>
                      <div>
                        <div class="fw-bold" style="font-size:13px">{{ displayName(tpl.name) }}</div>
                        <small class="text-muted" style="font-family:monospace;font-size:10px">{{ tpl.name }}</small>
                      </div>
                    </td>
                    <td>
                      <span class="badge-cat" :class="categoryClass(tpl.category)">
                        {{ categoryLabel(tpl.category) }}
                      </span>
                    </td>
                    <td><span class="text-muted" style="font-size:12px">{{ tpl.language_code || 'pt_BR' }}</span></td>
                    <td>
                      <div class="d-flex align-items-center gap-1">
                        <span class="status-dot" :class="statusClass(tpl.status)"></span>
                        <span class="status-label" :class="statusClass(tpl.status)">{{ statusLabel(tpl.status) }}</span>
                      </div>
                      <small v-if="tpl.rejected_reason" class="text-danger" style="font-size:10px">{{ tpl.rejected_reason }}</small>
                    </td>
                    <td><span class="text-muted" style="font-size:12px">{{ formatDate(tpl.updated_at) }}</span></td>
                    <td>
                      <div class="d-flex gap-1 justify-content-end">
                        <button class="btn btn-outline-secondary btn-sm" title="Preview" @click.stop="openPreview(tpl)">
                          <i class="bx bx-show"></i>
                        </button>
                        <button v-if="isApproved(tpl)" class="btn btn-outline-success btn-sm" title="Disparar WhatsApp de teste" @click.stop="openTestSend(tpl)">
                          <i class="bx bx-send"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                  <tr v-if="filteredTemplates.length === 0">
                    <td colspan="6" class="text-center text-muted" style="padding:60px 20px;vertical-align:middle">
                      <i class="bx bx-message-square-detail d-block mx-auto" style="font-size:48px;opacity:.25"></i>
                      <div class="mt-3" style="font-size:15px;font-weight:500;color:#495057">Nenhum template encontrado</div>
                      <div v-if="canCreate" class="mt-1" style="font-size:13px;color:#6c757d">Clique em "Sincronizar" para importar da Meta ou "Criar template" para criar um novo.</div>
                      <div v-else class="mt-1" style="font-size:13px;color:#6c757d">Os templates disponíveis na conta Positus integrada serão listados automaticamente.</div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Pagination -->
            <div class="row" v-if="pagination.total > perPage">
              <div class="col">
                <div class="dataTables_paginate paging_simple_numbers float-right">
                  <ul class="pagination pagination-rounded mb-0">
                    <b-pagination v-model="currentPage" :total-rows="pagination.total" :per-page="perPage" @input="updateList"></b-pagination>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Sidebar Preview -->
    <b-sidebar id="sidebar-preview" right shadow backdrop bg-variant="white" v-model="showPreview" width="380px">
      <div class="px-3 py-2">
        <h6 class="mb-3">Preview do Template</h6>
        <TemplatePreview
          v-if="previewTemplate"
          :headerType="previewHeaderType"
          :headerText="previewHeaderText"
          :headerImageUrl="previewHeaderImageUrl"
          :headerVideoUrl="previewHeaderVideoUrl"
          :headerDocumentName="previewHeaderDocumentName"
          :bodyText="previewBody"
          :footerText="previewFooter"
          :buttons="previewButtons"
          :examples="{}"
        />
        <div v-if="previewTemplate" class="mt-3 p-3" style="background:#f8f9fa;border-radius:6px">
          <small class="text-muted d-block mb-1">Nome completo</small>
          <code style="font-size:11px">{{ previewTemplate.name }}</code>
          <small class="text-muted d-block mt-2 mb-1">Categoria</small>
          <span class="badge-cat" :class="categoryClass(previewTemplate.category)">{{ categoryLabel(previewTemplate.category) }}</span>
          <small class="text-muted d-block mt-2 mb-1">Status</small>
          <div class="d-flex align-items-center gap-1">
            <span class="status-dot" :class="statusClass(previewTemplate.status)"></span>
            <span class="status-label" :class="statusClass(previewTemplate.status)">{{ statusLabel(previewTemplate.status) }}</span>
          </div>
        </div>
      </div>
    </b-sidebar>

    <!-- Modal disparo de teste -->
    <TemplateTestModal ref="testModal" />
  </Layout>
</template>

<script>
import Swal from 'sweetalert2'
import Layout from '@/components/layout/vertical'
import PageHeader from '@/components/page-header'
import TemplatePreview from './components/TemplatePreview'
import TemplateTestModal from './components/TemplateTestModal'
import appConfig from '@/app.config'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import { mapActions, mapState } from 'vuex'
import { getMode } from './services'

export default {
  components: { Layout, PageHeader, TemplatePreview, TemplateTestModal },
  name: 'WATemplates',
  page: {
    title: 'Templates WhatsApp',
    meta: [{ name: 'description', content: appConfig.description }]
  },
  data () {
    return {
      search: '',
      statusFilter: '',
      syncing: false,
      showPreview: false,
      previewTemplate: null,
      canCreate: true,
      templateMode: 'meta',
      currentPage: 1,
      perPage: 20,
      title: 'Templates WhatsApp',
      items: [
        { text: 'WhatsApp' },
        { text: 'Templates', active: true }
      ]
    }
  },
  mounted () {
    this.loadMode()
    this.updateList()
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('template', ['templates', 'pagination']),
    filteredTemplates () {
      let list = this.templates || []
      if (this.statusFilter) {
        list = list.filter(t => t.status === this.statusFilter)
      }
      return list
    },
    previewHeaderType () {
      if (!this.previewTemplate) return ''
      // QRCode dinâmico é salvo como HEADER format IMAGE + dynamic_header_type
      if (this.previewTemplate.dynamic_header_type === 'participant_qrcode') return 'QRCODE_PARTICIPANT'
      const c = (this.previewTemplate.components || []).find(x => x.type === 'HEADER')
      return c ? c.format : ''
    },
    previewHeaderImageUrl () {
      return this.previewTemplate ? (this.previewTemplate.header_image_url || '') : ''
    },
    previewHeaderVideoUrl () {
      return this.previewTemplate ? (this.previewTemplate.header_video_url || '') : ''
    },
    previewHeaderDocumentName () {
      const url = this.previewTemplate ? this.previewTemplate.header_document_url : ''
      if (!url) return ''
      return url.split('/').pop().split('?')[0]
    },
    previewHeaderText () {
      if (!this.previewTemplate) return ''
      const c = (this.previewTemplate.components || []).find(x => x.type === 'HEADER')
      return c && c.format === 'TEXT' ? (c.text || '') : ''
    },
    previewBody () {
      if (!this.previewTemplate) return ''
      const c = (this.previewTemplate.components || []).find(x => x.type === 'BODY')
      return c ? (c.text || '') : ''
    },
    previewFooter () {
      if (!this.previewTemplate) return ''
      const c = (this.previewTemplate.components || []).find(x => x.type === 'FOOTER')
      return c ? (c.text || '') : ''
    },
    previewButtons () {
      if (!this.previewTemplate) return []
      const c = (this.previewTemplate.components || []).find(x => x.type === 'BUTTONS')
      return c ? (c.buttons || []) : []
    }
  },
  methods: {
    ...mapActions('template', {
      getAll: 'getAll',
      syncMeta: 'syncFromMeta',
      refreshStatusAction: 'refreshStatus',
      deleteAction: 'deleteTemplate'
    }),
    displayName (name) {
      // Remove prefixo do evento (ex: arcoday2_confirmacao → confirmacao)
      const parts = name.split('_')
      if (parts.length > 1) {
        return parts.slice(1).join('_')
      }
      return name
    },
    categoryLabel (cat) {
      const map = { MARKETING: 'Marketing', UTILITY: 'Utilidade', AUTHENTICATION: 'Autenticação' }
      return map[cat] || cat || '—'
    },
    categoryClass (cat) {
      const map = { MARKETING: 'cat-marketing', UTILITY: 'cat-utility', AUTHENTICATION: 'cat-auth' }
      return map[cat] || ''
    },
    statusLabel (status) {
      const map = { APPROVED: 'Aprovado', PENDING: 'Pendente', REJECTED: 'Rejeitado', PAUSED: 'Pausado', DISABLED: 'Desativado' }
      return map[status] || status || '—'
    },
    statusClass (status) {
      const map = { APPROVED: 'st-approved', PENDING: 'st-pending', REJECTED: 'st-rejected', PAUSED: 'st-paused', DISABLED: 'st-disabled' }
      return map[status] || ''
    },
    formatDate (date) {
      if (!date) return '—'
      return new Date(date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    },
    async loadMode () {
      try {
        const res = await getMode()
        this.templateMode = res.data.mode
        this.canCreate = res.data.can_create
      } catch (e) {
        // Fallback: permite criação
      }
    },
    openPreview (tpl) {
      this.previewTemplate = tpl
      this.showPreview = true
    },
    isApproved (tpl) {
      return String(tpl.status).toUpperCase() === 'APPROVED'
    },
    openTestSend (tpl) {
      this.$refs.testModal.open(tpl)
    },
    editTemplate (tpl) {
      if (tpl.status === 'PENDING') {
        errorMessage('Template em análise. Aguarde aprovação ou rejeição para editar.')
        return
      }
      this.$router.push(`/wa/template/${tpl.id}/edit`)
    },
    async handleSync () {
      this.syncing = true
      try {
        const res = await this.syncMeta()
        successMessage(`${res.data.synced} template(s) sincronizado(s).`, 2500)
        this.updateList()
      } catch (err) {
        errorMessage('Falha ao sincronizar: ' + (err.response?.data?.message || err.message))
      } finally {
        this.syncing = false
      }
    },
    async handleRefresh (tpl) {
      this.$set(tpl, '_refreshing', true)
      try {
        await this.refreshStatusAction(tpl.id)
        successMessage('Status atualizado.', 1500)
        this.updateList()
      } catch (err) {
        errorMessage('Falha ao atualizar status.')
      } finally {
        this.$set(tpl, '_refreshing', false)
      }
    },
    handleDelete (tpl) {
      Swal.fire({
        title: 'Excluir template?',
        html: `O template <b>${this.displayName(tpl.name)}</b> será removido da Meta e do sistema.<br><small class="text-muted">O nome não poderá ser reutilizado por 30 dias.</small>`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#f46a6a',
        cancelButtonColor: '#4a4848',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Sim, excluir'
      }).then(async (result) => {
        if (result.value) {
          try {
            await this.deleteAction(tpl.id)
            successMessage('Template excluído com sucesso.', 2500)
            this.updateList()
          } catch (err) {
            errorMessage('Falha ao excluir: ' + (err.response?.data?.message || err.message))
          }
        }
      })
    },
    doSearch () {
      this.currentPage = 1
      this.updateList()
    },
    updateList () {
      const params = {
        search: this.search || null,
        limit: this.perPage,
        page: this.currentPage
      }
      let loader = this.$loading.show()
      this.getAll(params).then(() => {
        loader.hide()
      }).catch(() => {
        loader.hide()
      })
    }
  }
}
</script>

<style scoped>
.link { cursor: pointer; }
.table-responsive { min-height: 300px; }
.float-sm-end { float: right; }
.gap-2 { gap: 0.5rem; }
.gap-1 { gap: 0.25rem; }

.tpl-icon {
  width: 34px;
  height: 34px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
  background: #EEF1FE;
  color: #4A6CF7;
}

.tpl-icon.cat-marketing { background: #FCE7F3; color: #BE185D; }
.tpl-icon.cat-utility { background: #EEF1FE; color: #4A6CF7; }
.tpl-icon.cat-auth { background: #F0FDF4; color: #22C55E; }

.badge-cat {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .3px;
}

.badge-cat.cat-marketing { background: #FCE7F3; color: #BE185D; }
.badge-cat.cat-utility { background: #EEF1FE; color: #4A6CF7; }
.badge-cat.cat-auth { background: #F0FDF4; color: #22C55E; }

.status-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  flex-shrink: 0;
}

.status-dot.st-approved { background: #22C55E; }
.status-dot.st-pending { background: #F59E0B; }
.status-dot.st-rejected { background: #EF4444; }
.status-dot.st-paused { background: #F97316; }
.status-dot.st-disabled { background: #94A3B8; }

.status-label {
  font-size: 11.5px;
  font-weight: 600;
}

.status-label.st-approved { color: #22C55E; }
.status-label.st-pending { color: #F59E0B; }
.status-label.st-rejected { color: #EF4444; }
.status-label.st-paused { color: #F97316; }
.status-label.st-disabled { color: #94A3B8; }
</style>
